import { useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { ChevronLeftIcon, ChevronRightIcon, ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { useReceipts, type ReceiptListItem } from "../lib/receipts";
import { ReceiptCard } from "../components/ReceiptCard";
import { ReceiptListExport } from "../components/ReceiptListExport";
import { SignedAmount } from "../components/SignedAmount";
import { AxisTree } from "../components/AxisTree";
import { monthName } from "../lib/format";

// Track G / Variante 1 (2026-06-05): Archiv hat zwei komplementäre Sichten auf
// dasselbe Belegset (`reviewedAt IS NOT NULL`):
//   - „Nach Zeit"      → Jahr → Monat → Liste (heute Default)
//   - „Nach Kategorie" → 4-/5-Achsen-Tree (Verwendungsart › Richtung › Kostenart › Cat …)
// Tab-Auswahl persistiert in URL (`?view=kategorie`). „Nach Zeit" ist Default und
// braucht keinen Query-Param — ohne `view` ist die Sicht zeitlich.
type View = "zeit" | "kategorie";

export function ArchivPage() {
  const [params, setParams] = useSearchParams();
  const yearStr = params.get("year");
  const monthStr = params.get("month");
  const view: View = params.get("view") === "kategorie" ? "kategorie" : "zeit";
  const year = yearStr ? parseInt(yearStr, 10) : undefined;
  const month = monthStr ? parseInt(monthStr, 10) : undefined;

  const filters = useMemo(
    () => ({ year: month ? year : undefined, month }),
    [year, month],
  );

  // List-View (Year+Month): API-seitiger Filter. needsReview=false → nur geprüfte Bons
  // (F18: ungeprüfte leben in der Inbox, nicht im Archiv — Inbox=Queue, Archiv=erledigt).
  const listView = year !== undefined && month !== undefined;
  const listQuery = useReceipts(listView ? { year, month, reviewed: true } : {});

  // Drill-Index (Year-/Month-Übersicht): nur GEPRÜFTE Belege (reviewedAt gesetzt) — das Archiv zeigt
  // ausschließlich vom Menschen bestätigte Bons; ungeprüfte leben in der Inbox.
  const indexQuery = useReceipts({ reviewed: true });

  // Drill-Downs (Year/Month/List) bleiben in der Zeit-Achse — wer einen Monat anklickt,
  // will den Monat sehen, nicht zwischen Achsen wechseln. Tabs erscheinen nur auf der
  // Top-Level-Sicht (Year-Index oder Kategorie-Tree).
  if (listView) {
    return <MonthList year={year!} month={month!} query={listQuery} onBack={() => setParams({ year: String(year) })} />;
  }

  if (year !== undefined) {
    return <YearMonths year={year} query={indexQuery} onBack={() => setParams({})} onSelectMonth={(m) => setParams({ year: String(year), month: String(m) })} />;
  }

  // Tab-Wechsel: setzt komplette Query neu (keine View-übergreifenden Filter mitschleppen).
  const onSwitchView = (v: View) => setParams(v === "kategorie" ? { view: "kategorie" } : {});

  if (view === "kategorie") {
    return <CategoryView activeView={view} onSwitchView={onSwitchView} />;
  }

  return (
    <YearIndex
      query={indexQuery}
      onSelectYear={(y) => setParams({ year: String(y) })}
      activeView={view}
      onSwitchView={onSwitchView}
    />
  );
}

// ─── Tab-Auswahl Zeit/Kategorie ─────────────────────────────────────────────

function ViewTabs({ active, onSwitch }: { active: View; onSwitch: (v: View) => void }) {
  return (
    <div className="inline-flex rounded-ios bg-ios-bg/60 p-0.5 mb-4">
      {(["zeit", "kategorie"] as const).map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onSwitch(v)}
          className={`text-[13px] px-3 py-1 rounded-[7px] transition ${active === v ? "bg-white shadow-sm font-medium" : "text-ios-muted"}`}
        >
          {v === "zeit" ? "Nach Zeit" : "Nach Kategorie"}
        </button>
      ))}
    </div>
  );
}

// ─── Ebene 1: Jahresübersicht (Tab „Nach Zeit") ──────────────────────────────

function YearIndex({
  query,
  onSelectYear,
  activeView,
  onSwitchView,
}: {
  query: ReturnType<typeof useReceipts>;
  onSelectYear: (year: number) => void;
  activeView: View;
  onSwitchView: (v: View) => void;
}) {
  const { data, isLoading } = query;

  const groups = useMemo(() => {
    if (!data?.receipts) return { years: new Map<number, ReceiptListItem[]>(), undated: [] as ReceiptListItem[] };
    const years = new Map<number, ReceiptListItem[]>();
    const undated: ReceiptListItem[] = [];
    for (const r of data.receipts) {
      if (!r.receiptDate) {
        undated.push(r);
        continue;
      }
      const y = new Date(r.receiptDate).getUTCFullYear();
      if (!years.has(y)) years.set(y, []);
      years.get(y)!.push(r);
    }
    return { years, undated };
  }, [data]);

  const yearList = useMemo(
    () => [...groups.years.entries()].sort((a, b) => b[0] - a[0]),
    [groups.years],
  );

  return (
    <Layout title="Archiv" subtitle="Alle erfassten Quittungen">
      <ViewTabs active={activeView} onSwitch={onSwitchView} />
      {isLoading ? (
        <CardPlaceholder>Lade…</CardPlaceholder>
      ) : yearList.length === 0 && groups.undated.length === 0 ? (
        <CardPlaceholder icon={ArchiveBoxIcon}>Noch keine Quittungen erfasst.</CardPlaceholder>
      ) : (
        <ul className="ios-card divide-y divide-ios-line/60 overflow-hidden">
          {yearList.map(([y, items]) => (
            <li key={y}>
              <button
                type="button"
                onClick={() => onSelectYear(y)}
                className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-ios-bg/40 transition"
              >
                <div>
                  <div className="text-[18px] font-semibold">{y}</div>
                  <div className="text-[13px] text-ios-muted">
                    {items.length} {items.length === 1 ? "Quittung" : "Quittungen"} · <SignedAmount value={sumTotals(items)} className="text-[13px]" />
                  </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-ios-subtle" />
              </button>
            </li>
          ))}
          {groups.undated.length > 0 && (
            <li className="px-5 py-3 text-[13px] text-ios-muted">
              {groups.undated.length} ohne Datum
            </li>
          )}
        </ul>
      )}
    </Layout>
  );
}

// ─── Tab „Nach Kategorie" — inhaltliche Hierarchie via AxisTree ─────────────

function CategoryView({ activeView, onSwitchView }: { activeView: View; onSwitchView: (v: View) => void }) {
  return (
    <Layout title="Archiv" subtitle="Belege nach Verwendungsart, Richtung, Kostenart, Kategorie">
      <ViewTabs active={activeView} onSwitch={onSwitchView} />
      <div className="ios-card p-3">
        {/* Tree liest /api/reports/tree (nur reviewedAt-Belege — identisch mit
            der Zeit-Sicht). Klick auf einen Knoten navigiert auf die existierende
            Drill-Down-Liste, die die URL-Achsen-Filter versteht. */}
        <AxisTree targetPath="/auswertungen/liste" />
      </div>
    </Layout>
  );
}

// ─── Ebene 2: Monatsübersicht für ein Jahr ──────────────────────────────────

function YearMonths({
  year,
  query,
  onBack,
  onSelectMonth,
}: {
  year: number;
  query: ReturnType<typeof useReceipts>;
  onBack: () => void;
  onSelectMonth: (month: number) => void;
}) {
  const { data, isLoading } = query;

  const months = useMemo(() => {
    const m = new Map<number, ReceiptListItem[]>();
    if (!data?.receipts) return m;
    for (const r of data.receipts) {
      if (!r.receiptDate) continue;
      const d = new Date(r.receiptDate);
      if (d.getUTCFullYear() !== year) continue;
      const mo = d.getUTCMonth() + 1;
      if (!m.has(mo)) m.set(mo, []);
      m.get(mo)!.push(r);
    }
    return m;
  }, [data, year]);

  const monthList = useMemo(() => [...months.entries()].sort((a, b) => b[0] - a[0]), [months]);

  return (
    <Layout
      title={String(year)}
      subtitle={`${monthList.reduce((sum, [, items]) => sum + items.length, 0)} Quittungen`}
      back={{ onClick: onBack, label: "Archiv" }}
    >
      {isLoading ? (
        <CardPlaceholder>Lade…</CardPlaceholder>
      ) : monthList.length === 0 ? (
        <CardPlaceholder icon={ArchiveBoxIcon}>Keine Quittungen in {year}.</CardPlaceholder>
      ) : (
        <ul className="ios-card divide-y divide-ios-line/60 overflow-hidden">
          {monthList.map(([m, items]) => (
            <li key={m}>
              <button
                type="button"
                onClick={() => onSelectMonth(m)}
                className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-ios-bg/40 transition"
              >
                <div>
                  <div className="text-[17px] font-medium">{monthName(m)}</div>
                  <div className="text-[13px] text-ios-muted">
                    {items.length} {items.length === 1 ? "Quittung" : "Quittungen"} · <SignedAmount value={sumTotals(items)} className="text-[13px]" />
                  </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-ios-subtle" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Layout>
  );
}

// ─── Ebene 3: Liste in Monat ─────────────────────────────────────────────────

function MonthList({
  year,
  month,
  query,
  onBack,
}: {
  year: number;
  month: number;
  query: ReturnType<typeof useReceipts>;
  onBack: () => void;
}) {
  const { data, isLoading } = query;

  return (
    <Layout
      title={`${monthName(month)} ${year}`}
      subtitle={data ? <>{data.count} Quittungen · <SignedAmount value={sumTotals(data.receipts ?? [])} className="text-[15px]" /></> : ""}
      back={{ onClick: onBack, label: String(year) }}
    >
      {isLoading ? (
        <CardPlaceholder>Lade…</CardPlaceholder>
      ) : !data?.receipts.length ? (
        <CardPlaceholder icon={ArchiveBoxIcon}>Keine Quittungen.</CardPlaceholder>
      ) : (
        <ReceiptListExport receipts={data.receipts} />
      )}
    </Layout>
  );
}

// ─── Layout-Wrapper ──────────────────────────────────────────────────────────

function Layout({
  title,
  subtitle,
  back,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  back?: { onClick: () => void; label: string };
  children: React.ReactNode;
}) {
  return (
    <div className="px-5 py-6 md:px-10 md:py-8 max-w-5xl mx-auto animate-fade-in">
      {back && (
        <button
          type="button"
          onClick={back.onClick}
          className="inline-flex items-center gap-1 text-[15px] text-ios-blue hover:text-ios-blueHover mb-2"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          {back.label}
        </button>
      )}
      <header className="mb-5">
        <h1 className="text-[28px] md:text-[34px] font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-[15px] text-ios-muted mt-1">{subtitle}</p>}
      </header>
      {children}
    </div>
  );
}

function CardPlaceholder({
  icon: Icon,
  children,
}: {
  icon?: typeof ArchiveBoxIcon;
  children: React.ReactNode;
}) {
  return (
    <div className="ios-card p-10 text-center text-ios-muted">
      {Icon && <Icon className="w-10 h-10 mx-auto text-ios-subtle mb-3" />}
      <p className="text-[15px]">{children}</p>
    </div>
  );
}

function sumTotals(items: ReceiptListItem[]): number {
  return items.reduce((sum, r) => sum + (r.totalAmount ?? 0), 0);
}

// Inbox = noch nicht geprüfte Belege (reviewedAt IS NULL), in Scan-Reihenfolge.
// Erst „Prüfung abschließen" schiebt einen Beleg ins Archiv. (gleicher Stack, anderer Filter)
export function InboxPage() {
  const { data, isLoading } = useReceipts({ reviewed: false, sort: "capture" });

  return (
    <Layout title="Inbox" subtitle="Noch nicht geprüfte Quittungen — neueste zuerst">
      {isLoading ? (
        <CardPlaceholder>Lade…</CardPlaceholder>
      ) : !data?.receipts.length ? (
        <CardPlaceholder>
          Keine offenen Quittungen — alles geprüft 🎉
        </CardPlaceholder>
      ) : (
        <ReceiptListExport receipts={data.receipts} />
      )}
    </Layout>
  );
}

// „Zuletzt" = ALLE Belege (geprüft + ungeprüft) in Scan-/Upload-Reihenfolge — eine eigene Zeitachse,
// die weder Inbox (nur ungeprüft) noch Archiv (nach Belegdatum) abdeckt.
export function ZuletztPage() {
  const { data, isLoading } = useReceipts({ sort: "capture" });

  return (
    <Layout title="Zuletzt" subtitle="Alle Belege in Scan-Reihenfolge — neueste zuerst">
      {isLoading ? (
        <CardPlaceholder>Lade…</CardPlaceholder>
      ) : !data?.receipts.length ? (
        <CardPlaceholder icon={ArchiveBoxIcon}>Noch keine Belege.</CardPlaceholder>
      ) : (
        <ReceiptListExport receipts={data.receipts} />
      )}
    </Layout>
  );
}
